/**
 * 新デプロイ検知 → ユーザーにリロード提案 (GRAND Designer と同一方式)
 *
 * 仕組み:
 * 1. ビルド時、vite.config.ts が `__BUILD_VERSION__` 定数を埋め込み +
 *    `dist/version.json` を書き出す (両方とも同じバージョン)
 * 2. フロントは起動時に `__BUILD_VERSION__` を覚える (= 現在ロード中のバージョン)
 * 3. 定期的に `/version.json` を fetch して比較
 *    一致しなければ「新バージョンが利用できます」と通知
 *
 * ローカル開発時 (Vite dev server):
 *   - `/version.json` は dist/ にしか出ないため、dev では 404 になる
 *   - その場合は黙ってスキップ (新バージョン検知は本番のみ機能)
 */
import { onBeforeUnmount, onMounted, readonly, ref } from 'vue';

interface VersionInfo {
  version: string;
  buildTime: string;
}

/** 5 分ごとに version.json をポーリング */
const POLL_INTERVAL_MS = 5 * 60 * 1000;
/** 起動直後の最初のチェックまでの遅延 (起動負荷を避ける) */
const INITIAL_DELAY_MS = 30 * 1000;

// 複数コンポーネントから呼ばれても同じ state を共有する (singleton)
const newVersionAvailable = ref(false);
const latestVersion = ref<string | null>(null);
const currentVersion =
  typeof __BUILD_VERSION__ === 'string' ? __BUILD_VERSION__ : 'dev';

let timerId: ReturnType<typeof setInterval> | null = null;
let initialTimeoutId: ReturnType<typeof setTimeout> | null = null;
let visibilityHandler: (() => void) | null = null;
let listenerCount = 0;

async function checkVersion(): Promise<void> {
  // 既に新バージョン検知済なら追加 fetch 不要
  if (newVersionAvailable.value) return;
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return; // dev (404) など — 黙ってスキップ
    const data = (await res.json()) as VersionInfo;
    if (data.version && data.version !== currentVersion) {
      newVersionAvailable.value = true;
      latestVersion.value = data.version;
      console.log(
        `🚀 新バージョン検知: ${currentVersion} → ${data.version} (built: ${data.buildTime})`,
      );
      // 検知後はポーリング停止 (再チェック不要)
      stopPolling();
    }
  } catch {
    // ネットワークエラー等は無視
  }
}

function startPolling(): void {
  // 起動直後 30 秒で最初の 1 回
  initialTimeoutId = setTimeout(() => {
    void checkVersion();
  }, INITIAL_DELAY_MS);
  // 5 分間隔で継続チェック
  timerId = setInterval(() => {
    void checkVersion();
  }, POLL_INTERVAL_MS);
}

function stopPolling(): void {
  if (initialTimeoutId) {
    clearTimeout(initialTimeoutId);
    initialTimeoutId = null;
  }
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
}

function onVisibilityChange(): void {
  // タブがフォアグラウンドに戻ったら即時チェック (頻繁にタブ切替するユーザー向け)
  if (document.visibilityState === 'visible') {
    void checkVersion();
  }
}

function reload(): void {
  // ハードリロード (最新の index.html を取得し、新しい JS バンドルがロードされる)
  window.location.reload();
}

export function useDeployCheck() {
  onMounted(() => {
    listenerCount++;
    if (listenerCount === 1) {
      // 最初のリスナーで polling 開始
      startPolling();
      visibilityHandler = onVisibilityChange;
      document.addEventListener('visibilitychange', visibilityHandler);
    }
  });

  onBeforeUnmount(() => {
    listenerCount--;
    if (listenerCount === 0) {
      stopPolling();
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
        visibilityHandler = null;
      }
    }
  });

  return {
    newVersionAvailable: readonly(newVersionAvailable),
    latestVersion: readonly(latestVersion),
    currentVersion,
    reload,
    checkNow: checkVersion,
  };
}
